import pkg from '../../package.json';
import {
  AppEnvironment,
  EnvironmentConfig,
  EnvironmentName,
} from '../models/common/environment/environment';

let appEnvironment: AppEnvironment;
let loadEnvironmentPromise: Promise<AppEnvironment>;

const defaultEnvironmentConfig: EnvironmentConfig = {
  name: getDefaultEnvironmentName(),
  projectName: 'VS Agenda',
  projectVersion: pkg.version,
  projectBuildDate: Date.now(),
  osInterfaceURL: 'http://localhost:3300',
  apiUrl: 'https://api.agenda.homolog.vsd.app.br/v1',
  apiCentralURL: 'https://api.central.homolog.vsd.app.br/v1/',
  keycloakUrl: 'https://auth.homolog.vsd.app.br',
  apiCentralSoftwareId: '01KKPBGH02VB3KJ6T4GH7VPAXJ',
  apiSenhasSoftwareId: '01KKPBGH02VB3KJ6T4GH7VPAXK',
};

export function makeEnvironment() {
  if (!appEnvironment) {
    appEnvironment = buildAppEnvironment(defaultEnvironmentConfig);
  }

  return appEnvironment;
}

export async function loadEnvironmentConfig(): Promise<AppEnvironment> {
  if (!loadEnvironmentPromise) {
    loadEnvironmentPromise = resolveEnvironmentConfig().then((config) => {
      appEnvironment = buildAppEnvironment(config);
      console.log('Runtime environment loaded', appEnvironment);
      return appEnvironment;
    });
  }

  return loadEnvironmentPromise;
}

function buildAppEnvironment(
  config: Partial<EnvironmentConfig>,
): AppEnvironment {
  const mergedConfig = mergeEnvironmentConfig(config);
  const nextEnvironment = new AppEnvironment();

  nextEnvironment.ENV_NAME = mergedConfig.name;
  nextEnvironment.PROJECT_NAME = mergedConfig.projectName;
  nextEnvironment.PROJECT_VERSION = mergedConfig.projectVersion;
  nextEnvironment.PROJECT_BUILD_DATE = Number(mergedConfig.projectBuildDate);
  nextEnvironment.OS_INTERFACE_URL = mergedConfig.osInterfaceURL;
  nextEnvironment.API_URL = mergedConfig.apiUrl;
  nextEnvironment.API_CENTRAL_URL = mergedConfig.apiCentralURL;
  nextEnvironment.KEYCLOAK_URL = mergedConfig.keycloakUrl;
  nextEnvironment.API_CENTRAL_SOFTWARE_ID = mergedConfig.apiCentralSoftwareId;
  nextEnvironment.API_SENHAS_SOFTWARE_ID = mergedConfig.apiSenhasSoftwareId;
  return nextEnvironment;
}

async function resolveEnvironmentConfig(): Promise<EnvironmentConfig> {
  const runtimeConfig =
    (await fetchEnvironmentAsset('assets/config.json')) ?? {};

  return mergeEnvironmentConfig(runtimeConfig);
}

async function fetchEnvironmentAsset(
  relativePath: string,
): Promise<Partial<EnvironmentConfig> | undefined> {
  const assetUrl = new URL(relativePath, window.document.baseURI).toString();

  try {
    const response = await fetch(assetUrl, { cache: 'no-store' });

    if (!response.ok) {
      return undefined;
    }

    return (await response.json()) as Partial<EnvironmentConfig>;
  } catch (error) {
    console.warn(`Unable to load runtime config from ${relativePath}`, error);
    return undefined;
  }
}

function mergeEnvironmentConfig(
  runtimeConfig: Partial<EnvironmentConfig>,
): EnvironmentConfig {
  return {
    ...defaultEnvironmentConfig,
    ...runtimeConfig,
  };
}

function getDefaultEnvironmentName(): EnvironmentName {
  if (typeof window === 'undefined') {
    return EnvironmentName.DEV;
  }

  const host = window.location.host.toLowerCase();

  if (host.includes('localhost') || window.location.port === '4204') {
    return EnvironmentName.DEV;
  }

  if (host.includes('homolog')) {
    return EnvironmentName.TEST;
  }

  return EnvironmentName.PROD;
}
